import { ConnectingLineHandlerUtils } from "./ConnectingLineHandlerUtils";
import {
    ENDPOINT_RADIUS, GRID_CELL_CLICKABLE_ELEMENTS, ENDPOINT_DIRECTIONS_OBJECT, DRAGGABLE_ARROW_ENDPOINT_RADIUS
} from "../components/cell/grid-cell.config";
import { Endpoint } from "../components/endpoints/Endpoint";
import { StartEndpoint } from "../components/endpoints/StartEndpoint";
import { ConnectionLine } from "../components/endpoints/ConnectingLine";
import { pointInCircle } from "../components/cell/grid-cell.utils";
import { LineClipping } from "../utils/LineClipping";
import { GridConfig } from "./GridConfig";
import { DiagramEventEmitter } from "../../../diagram/modules/DiagramEventEmitter";
import { DIAGRAM_EMIT_EVENTS } from "../../../diagram/modules/DiagramEvents";
import { LineSelection } from "./selection/LineSelection";
import { ConnectorsUtils } from "../utils/ConnectorsUtils";
import { AnchoredPluginsHandler } from "./AnchoredPluginsHandler";

export const ConnectingLineHandler = (function () {
    const instance = {};
    class ConnectingLineHandler {
        instanceUuid;

        connectors = new Map();

        cells = new Map();

        tempCells = [];

        utils;

        gridConfig;

        eventEmitter;

        anchoredPlugins = null;

        passedThroughCells = [];

        camera;

        readonly;

        ctx;

        grid;

        redrawListeners = [];

        connectorsUtils = new ConnectorsUtils();

        constructor(diagramInstanceUuid) {
            if (!instance[diagramInstanceUuid]) {
                // setting the singleton instance
                instance[diagramInstanceUuid] = this;
                this.instanceUuid = diagramInstanceUuid;
                this.utils = new ConnectingLineHandlerUtils(this.instanceUuid);
                this.anchoredPlugins = new AnchoredPluginsHandler(this.instanceUuid);
                this.gridConfig = new GridConfig(this.instanceUuid);
                this.eventEmitter = new DiagramEventEmitter(this.instanceUuid);
            }
            return instance[diagramInstanceUuid];
        }

        /**
         * @description Registers a cell inside the cells map. Is called when the gridcell is initialized
         * @param {String} uuid - cell uuid
         * @param {Object} layout - cell layout
         * @param {Object} state - cell state
         * @param {GridCell} cellRef  - Cell reference that needs to be registered
         * @param {Object} ctx  - canvas 2d ctx
         */
        registerCell(uuid, layout, state, cellRef, ctx) {
            if (!this.ctx) this.ctx = ctx;
            this.cells.set(uuid, {
                layout, state, ref: cellRef, outgoing: [], incoming: [], endpoints: [], draggableEndpoint: null
            });
            if (state && state.temporary) {
                this.tempCells.push(uuid);
            }
        }

        /**
         * @description Called by a GridCell when it gets updated
         * @param {String} uuid - cell uuid
         * @param {Object} layout - cell layout
         * @param {Object} state - cell state
         * @param {GridCell} cellRef - GridCell Reference
         */
        updateCell(uuid, layout, state, cellRef) {
            const cellData = this.cells.get(uuid);
            if (state) cellData.state = { ...cellData.state, ...state };
            if (layout) cellData.layout = { ...cellData.layout, ...layout };
            if (cellRef) cellData.ref = cellRef;
            this.cells.set(uuid, cellData);
            this.updateLinkedItems(uuid);
            if (this.tempCells.indexOf(uuid) !== -1 && !state.temporary) {
                this.tempCells.splice(this.tempCells.indexOf(uuid), 1);
            }
        }

        /**
         * @description Adds Other handlers as redraw listeners
         * @param {Object} instance - other handler that wants to lissen to cell redraw events (ex: CellIndependentPluginHandler )
         */
        addRedrawListener(instance) {
            this.redrawListeners.push(instance);
        }

        /**
         * @description deletes a cell reference
         */
        deleteCell(uuid) {
            this.cells.delete(uuid);
        }

        /**
         * @description A redraw request. Redraws all affected elements by the redrawing of the cell
         * @param { String } uuid - a cell uuid
         * @param { LabelHandler } labelHandler
         */
        redrawRequest(uuid, labelHandler) {
            const cell = this.cells.get(uuid);
            const lineSelection = new LineSelection(this.instanceUuid);
            const selectedLine = lineSelection.getSelected();

            this.performPartialUpdateOnAllIntersectingLines(uuid);
            if (labelHandler) {
                labelHandler.redrawLabels(cell.layout, this.ctx);
            }
            if (this.redrawListeners.length > 0) {
                this.redrawListeners.forEach(listener => {
                    listener.cellRedrawed(cell.ref);
                });
            }
            if (!cell.ref.hasContent()) return;
            if (cell.ref.getContainerInstance()) {
                this.drawEndpoints(cell, null, null, null, selectedLine);
                // this.setDisabledOverlay(cell.ref);
            }

            cell.outgoing.forEach(connectorUuid => {
                const selectEndPoint = connectorUuid === selectedLine;
                const connector = this.connectors.get(connectorUuid);
                const cellToDrawEndpointsInto = this.cells.get(connector.to);

                if (cellToDrawEndpointsInto.ref.hasContent()) {
                    this.drawEndpoints(cellToDrawEndpointsInto, cellToDrawEndpointsInto.state.readonlyDisabled ? !cellToDrawEndpointsInto.state.disabled : (cell.ref.state.selected || connector.instance.state.directSelection || connector.instance.state.active || selectEndPoint), null, { incoming: true, incomingConnector: connector });
                }
            });
            cell.incoming.forEach(connectorUuid => {
                const selectEndPoint = connectorUuid === selectedLine;
                const connector = this.connectors.get(connectorUuid);
                const cellToDrawEndpointsInto = this.cells.get(connector.from);
                if (cellToDrawEndpointsInto.ref.hasContent()) {
                    const connectToCell = this.cells.get(connector.to);
                    const { path } = connector.instance.layout;
                    const connectorStartsFrom = this.connectorsUtils.extractCurrentEndpointsFromLine(cellToDrawEndpointsInto.ref, connectToCell.ref, path[0], path[path.length - 1]);
                    this.drawEndpoints(cellToDrawEndpointsInto, cellToDrawEndpointsInto.state.readonlyDisabled ? !cellToDrawEndpointsInto.state.disabled : (cell.ref.state.selected || connector.instance.state.active || connector.instance.state.directSelection || selectEndPoint), Object.keys(connectorStartsFrom.start)[0]);
                }
            });
            const contentInstance = cell.ref.getContentInstance();
            if (contentInstance && contentInstance.points.start > cell.outgoing.length > 0) {
                this.drawDraggableEndpoint(cell);
            }
            // this.performPartialUpdateOnAllIntersectingLines(uuid);
        }

        /**
         * @description Returns a plugin with the provided uuid
         */
        getPlugin(uuid, instance, getContentInstance) {
            for (let i = 0; i < this.connectors.keys().length; i++) {
                const connector = this.connectors.keys()[i];
                if (connector.instance.uuid === uuid) return connector.instance;
            }
            return this.getCellWithContentUUID(uuid, instance, getContentInstance);
        }

        /**
         * @description Returns a cell with the provided content uuid
         */
        getCellWithContentUUID(uuid, instance, getContentInstance) {
            const keys = this.cells.keys();
            for (const key of keys) {
                if (getContentInstance) {
                    const foundContent = this.cells.get(key).ref && this.cells.get(key).ref.getContentInstance() && this.cells.get(key).ref.getContentInstance().uuid === uuid;
                    if (foundContent) return this.cells.get(key).ref.getContentInstance();
                }
                if (this.cells.get(key).ref.hasContent() && this.cells.get(key).ref.getContentInstance().uuid === uuid) {
                    return instance ? this.cells.get(key).ref : this.cells.get(key).ref.uuid;
                }
            }
        }

        /**
         * @description updates linked items to the cell with the provided uuid
         * @param {SString} uuid
         */
        updateLinkedItems(uuid) {
            const cell = this.cells.get(uuid);
            [...cell.outgoing, ...cell.incoming].forEach(connectorUuid => {
                const connector = this.connectors.get(connectorUuid);
                if (!connector) return;
                connector.instance.update({}, { ...cell.ref.state });
                const toPerformPartialUpdates = [];
                connector.passesThrough.forEach((celluuid) => {
                    // let cell = this.cells.get(celluuid);
                    // if (cell.ref.hasContent()) {
                    // cell.ref.draw(true);
                    // }
                    toPerformPartialUpdates.push(celluuid);
                });
                // this.cells.get(connector.to).ref.draw(true);
                toPerformPartialUpdates.forEach(celluuid => {
                    this.performPartialUpdateOnAllIntersectingLines(celluuid);
                });
                this.updateEndpoints(cell, cell.ref.state, false, connector);
                const toCell = this.cells.get(connector.to);
                this.updateEndpoints(toCell, cell.ref.state, true, connector);
                // toCell.ref.draw(true);
            });
        }

        /**
         * @description updates the endpoints of the provided cell
         */
        updateEndpoints(cell, state, secondary, connector) {
            if (cell.endpoints) {
                for (let i = 0; i < cell.endpoints.length; i++) {
                    const endpoint = cell.endpoints[i];
                    let active = false;
                    if (secondary) active = state.selected || connector.instance.state.directSelection || connector.instance.state.active;
                    else active = state.selected || (state.connectingLineHover) || connector.instance.state.directSelection || connector.instance.state.active;
                    if (secondary && endpoint.layout.side !== connector.instance.layout.endSide) {
                        active = false;
                    }
                    if (connector && connector.instance.state && connector.instance.state.active) {
                        active = true;
                    }
                    endpoint.update({}, { active });
                }
            }
        }

        /**
         * @description Checks if a cell is registered
         * @param {String} uuid
         */
        isRegistered(uuid) {
            return !!this.cells.get(uuid);
        }

        /**
         * @description Performs partial update on all intersecting lines with the cell having the provided uuid
         */
        performPartialUpdateOnAllIntersectingLines(cellUuid) {
            const partialUpdatesNecessary = [];
            if (this.camera && this.utils.cellOutsideViewport(cellUuid, this.cells, this.camera)) {
                return;
            }
            if (this.passedThroughCells.indexOf(cellUuid) !== -1) {
                this.connectors.forEach(connector => {
                    if (connector.passesThrough && connector.passesThrough.indexOf(cellUuid) !== -1) {
                        partialUpdatesNecessary.push(connector);
                    }
                });
            }
            const lineClipper = new LineClipping();
            const cell = this.cells.get(cellUuid);
            const { layout } = cell.ref;
            const lineWidth = layout.lineWidth || 1;
            const rectangular = [
                layout.x - lineWidth,
                layout.y - lineWidth,
                layout.x + this.gridConfig.cellWidth + (lineWidth * 2),
                layout.y + this.gridConfig.cellHeight + (lineWidth * 2)];
            for (let i = 0; i < partialUpdatesNecessary.length; i++) {
                const { path } = partialUpdatesNecessary[i].instance.layout;
                const matrixPath = this.utils.turnObjectPathIntoMatrixPath(path);
                const intersection = lineClipper.lineclip(matrixPath, rectangular);
                const objectIntersection = this.utils.turnMatrixIntoObjectPath(intersection);
                let connectorToCellState; let
                    connectorFromCellState;
                const cellToConnect = this.cells.get(partialUpdatesNecessary[i].to);
                const cellFromConnect = this.cells.get(partialUpdatesNecessary[i].from);
                connectorToCellState = cellToConnect ? cellToConnect.ref.state : null;
                connectorFromCellState = cellFromConnect ? cellFromConnect.ref.state : null;
                partialUpdatesNecessary[i].instance.partialUpdate(objectIntersection, connectorFromCellState, connectorToCellState);
            }
        }

        /**
         * @description sets the provided @DiagramCamera refernece
         */
        setCamera(cam) {
            this.camera = cam;
        }

        /**
         * @description updates a connectors path with the newr provided one
         */
        updateConnectorPathV2(uuid, pathObj, grid, fromCellChange, toCellChange) {
            const connector = this.connectors.get(uuid);
            connector.instance.update({ endSide: pathObj.side, path: pathObj.path }, {});

            // this.redrawPassedThroughCells(connector.passesThrough);
            const passesThrough = this.utils.uniqueArrayItems([...this.getPassesThroughCells(pathObj, grid)].map(cell => cell.uuid));
            connector.passesThrough = passesThrough;
            this.setAllPassedThroughCells(passesThrough);
            if (fromCellChange) {
                const oldFromCellData = this.cells.get(connector.from);
                const connectorIndex = oldFromCellData.outgoing.indexOf(connector.instance.uuid);
                if (connectorIndex !== -1) oldFromCellData.outgoing.splice(connectorIndex, 1);
                this.cells.set(connector.from, oldFromCellData);
                connector.from = fromCellChange;
                const newCellData = this.cells.get(connector.from);
                newCellData.outgoing.push(connector.instance.uuid);
                this.cells.set(connector.from, newCellData);
            }
            if (toCellChange) {
                const oldToCellData = this.cells.get(connector.to);
                const connectorIndex = oldToCellData.incoming.indexOf(connector.instance.uuid);
                if (connectorIndex !== -1) oldToCellData.incoming.splice(connectorIndex, 1);
                this.cells.set(connector.to, oldToCellData);
                connector.to = toCellChange;
                const newCellData = this.cells.get(connector.to);
                newCellData.incoming.push(connector.instance.uuid);
                this.cells.set(connector.to, newCellData);
            }
            this.connectors.set(uuid, connector);
            connector.instance.draw();
            return connector.passesThrough;
        }

        /**
         * @description deletes the connector with the provided connectorUuid
         */
        deleteConnector(connectorUuid) {
            const connector = this.connectors.get(connectorUuid);
            const fromCell = this.cells.get(connector.from);
            const toCell = this.cells.get(connector.to);

            const outgoingIndex = fromCell.outgoing.indexOf(connectorUuid);
            fromCell.outgoing.splice(outgoingIndex, 1);

            const incomingIndex = toCell.incoming.indexOf(connectorUuid);
            toCell.incoming.splice(incomingIndex);
            connector.passesThrough.forEach((cellUuid) => {
                this.cells.get(cellUuid).ref.draw(true);
            });
            const toKeep = [];
            const connectorKeys = this.connectors.keys();
            for (const key of connectorKeys) {
                if (key !== connectorUuid) {
                    const conn = this.connectors.get(key);
                    for (let i = 0; i < connector.passesThrough.length; i++) {
                        if (conn.passesThrough.indexOf(connector.passesThrough[i]) !== -1) {
                            toKeep.push(connector.passesThrough[i]);
                        }
                    }
                }
            }
            const toDelete = connector.passesThrough.filter(elem => toKeep.indexOf(elem) !== -1);
            toDelete.forEach((cell) => {
                const index = this.passedThroughCells.indexOf(cell);
                // if (index !== -1) this.passedThroughCells.splice(index, 1);
            });
            this.connectors.delete(connectorUuid);
            // TODO: Emit event connector was deleted!
        }

        /**
         * @descriptino  redraws the cells with the provided cellUuid
         * @param {String[]} cellUuids
         */
        redrawPassedThroughCells(cellUuids) {
            cellUuids.forEach(uuid => {
                this.cells.get(uuid).ref.draw();
            });
        }

        /**
         * @description Gets the cells that the provided connectingLine passes through
         */
        getPassesThroughCells(connectingLine, grid) {
            const result = [];
            for (let i = 0; i < connectingLine.path.length; i++) {
                if (connectingLine.path[i] && connectingLine.path[i + 1]) {
                    const passCells = this.utils.getCellsInNeedOfUpdate(connectingLine.path[i], connectingLine.path[i + 1], true, grid);
                    result.push(...passCells);
                }
            }
            return result;
        }

        setAllPassedThroughCells(passesThroughArray) {
            this.passedThroughCells.push(...passesThroughArray);
            this.passedThroughCells = this.passedThroughCells.filter((elem, index, all) => all.indexOf(elem) === index);
        }

        /**
         * @description Registers a connection between two elements
         * @param {Object} connectingLine
         * @param {Boolean } fromPathDrag
         * @param {String} fromCellUuid
         * @param {String} toCellUuid
         * @param {GridCell[][]} grid
         * @param {Object} data
         * @param {LabelHandler} labelHandler
         * @param {Boolean} noEventEmission
         */
        createConnection(connectingLine, fromPathDrag, fromCellUuid, toCellUuid, grid, data, labelHandler, noEventEmission, viewInstance) {
            const line = new ConnectionLine({ path: connectingLine.path, endSide: connectingLine.side }, { selected: this.selected || fromPathDrag }, this.ctx, data || {}, labelHandler);
            let passesThrough = [...this.getPassesThroughCells(connectingLine, grid)];
            if (passesThrough) passesThrough = this.utils.uniqueArrayItems(passesThrough.map(cell => cell.uuid));
            else passesThrough = [];
            const currentConnCode = this.connectors.size ? (this.connectors.size + 1) : 1;
            const connector = {
                instance: line, from: fromCellUuid, to: toCellUuid, passesThrough, connectorCode: `con_${currentConnCode}`
            };
            let oldState;
            if (viewInstance) {
                oldState = viewInstance.getDiagramData();
            }
            // this.passedThroughCells.push(...passesThrough);
            this.setAllPassedThroughCells(passesThrough);
            if (!noEventEmission) {
                // this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONNECTOR_CREATED, { oldState, CONNECTOR_UUID: connector.instance.uuid }, true, false);
            }
            this.connectors.set(line.uuid, connector);
            const toCellData = this.cells.get(toCellUuid);
            toCellData.incoming.push(connector.instance.uuid);
            const fromCellData = this.cells.get(fromCellUuid);
            fromCellData.outgoing.push(connector.instance.uuid);
            toCellData.ref.draw();
            fromCellData.ref.draw();
            line.draw();
            const toCellContent = toCellData.ref.getContentInstance();
            const fromCellContent = fromCellData.ref.getContentInstance();
            if (!noEventEmission) {
                this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.CONNECTOR_CREATED,
                    {
                        oldState, CONNECTOR_UUID: connector.instance.uuid, FROM_UUID: fromCellContent.uuid, FROM_ID: fromCellContent.data.id, TO_UUID: toCellContent.uuid, TO_ID: toCellContent.data.id
                    }, false);
            }

            return line.uuid;
        }

        /**
         * @description gets the clicked cell eelement
         */
        getClickedCellElement(cellUuid, payload) {
            const cell = this.cells.get(cellUuid);
            const { draggableEndpoint } = cell;
            let insideDraggableEndpoint;
            if (draggableEndpoint) {
                insideDraggableEndpoint = pointInCircle(payload.x, payload.y, draggableEndpoint.layout.x, draggableEndpoint.layout.y, draggableEndpoint.layout.radius);
                if (insideDraggableEndpoint) {
                    return {
                        type: GRID_CELL_CLICKABLE_ELEMENTS.DRAGGABLE_ENDPOINT,
                        endpointLayout: draggableEndpoint.layout
                    };
                }
            }
        }

        /**
         * @description checks if the provided cell uuid has a line passing through it
         * @param {*} uuid
         */
        linePassesThroughCell(uuid) {
            return this.passedThroughCells.indexOf(uuid) !== -1;
        }

        /**
         * @description Gets the draggable endpoint instance from a cell with provided uuid
         * @param {*} cellUuid
         */
        getDraggableEndpointInstance(cellUuid) {
            const cell = this.cells.get(cellUuid);
            return cell.draggableEndpoint;
        }

        /**
         * @description returns the cell with the provided uuid
         * @param {*} cellUuid
         */
        getIncommingOutgoingInstance(cellUuid) {
            const cell = this.cells.get(cellUuid);
            return cell;
        }

        /**
         * @description Checks to see if a connector can connect to the cell with the provided uuid. If there are any more free end points on the provided cell.
         */
        canConnectTo(cellUuid) {
            const cell = this.cells.get(cellUuid);
            return (cell.ref.getContentInstance().getMaxEndPoints() - cell.incoming.length) > 0;
        }

        /**
         * @description Checks if the provided cell acces any more incoming connectors
         * @param {*} cell
         */
        allowsIncomingConnections(cell) {
            if (typeof cell === "string") cell = this.cells.get(cell);
            const currentIncoming = cell.incoming.length;
            const cellContent = cell.ref.getContentInstance();
            const maxIncoming = cellContent.points.end;
            return currentIncoming < maxIncoming;
        }

        /**
         * @description Draws the endpoints of the provided cell
         */
        drawEndpoints(cell, connectorSelected, incomingFromSide, incoming, selectedLine) {
            const endpointPositions = this.utils.getEndpointsPositions(cell);
            let sidesToDraw = ENDPOINT_DIRECTIONS_OBJECT;
            if (!cell.state.connectingLineHover) {
                // draw only endpoints connected to a incoming or outgoing line.
                if (incomingFromSide) {
                    const dir = {};
                    dir[sidesToDraw[incomingFromSide]] = sidesToDraw[incomingFromSide];
                    sidesToDraw = dir;
                } else {
                    sidesToDraw = this.utils.filterEndpointPositions(cell, this.connectors, endpointPositions);
                }
            }
            const endpoints = [];
            const cellState = cell.ref.state;

            for (const key in sidesToDraw) {
                const itsAlreadyCreated = cell.endpoints.find(endpoint => endpoint && endpoint.layout && endpoint.layout.side === key);
                const layout = {
                    x: endpointPositions[key].x, y: endpointPositions[key].y, side: key, radius: ENDPOINT_RADIUS
                };
                const state = {
                    active: (!cellState.disabled && cellState.readonlyDisabled) || cellState.selected || cellState.connectingLineHover || connectorSelected
                        || (typeof sidesToDraw[key] === "object" && sidesToDraw[key].uuid === selectedLine)
                };
                if (cellState.disabled && cellState.readonlyDisabled) state.readonlyDisabled = true;
                let endpoint;
                if (incoming && incoming.incomingConnector && layout.side !== incoming.incomingConnector.instance.layout.endSide) {
                    state.active = false;
                }
                if (incoming && incoming.incomingConnector && incoming.incomingConnector.instance.state.active) {
                    state.active = true;
                } else if (key && sidesToDraw[key] && sidesToDraw[key].uuid && !state.active) {
                    const connector = this.connectors.get(sidesToDraw[key].uuid);
                    state.active = connector.instance.state.active;
                }

                if (!itsAlreadyCreated) {
                    endpoint = new Endpoint(layout, state, this.ctx);
                    endpoints.push(endpoint);
                }
                if (itsAlreadyCreated) {
                    endpoint = itsAlreadyCreated;
                    endpoint.update(layout, state);
                }
                if (endpoint) endpoint.draw();
            }
            //
            cell.endpoints = endpoints;
            this.cells.set(cell.ref.uuid, cell);
        }

        /**
         * @description Resets the connecting line handler on dataSetChange
         */
        resetingDataSet() {
            this.cells = new Map();
            this.connectors = new Map();
            this.passedThroughCells = [];
            this.tempCells = [];
        }

        /**
         * @descriptions - draws the draggable endpoint of the provided cell
         */
        drawDraggableEndpoint(cell) {
            if (this.readonly) return;
            const { state } = cell.ref;
            const direction = state.hover;
            if (!direction || (direction && cell.draggableEndpoint && cell.draggableEndpoint.layout.dir === direction)) return;
            const position = this.utils.getDraggableEndpointPosition(cell, direction);
            const layout = { ...position, radius: DRAGGABLE_ARROW_ENDPOINT_RADIUS, dir: state.hover };
            const instance = new StartEndpoint(layout, { ...state }, this.ctx);
            instance.draw();
            cell.draggableEndpoint = instance;
            this.cells.set(cell.ref.uuid, cell);
        }

        /**
         * @description resets the references on the instance with the provided uuid
         * @param {*} instanceUuid
         */
        destroy(instanceUuid) {
            this.cells = new Map();
            this.connectors = new Map();
            this.passedThroughCells = [];
            this.redrawListeners = [];
            this.tempCells = [];
            this.camera = null;
            instance[instanceUuid] = null;
            delete instance[instanceUuid];
        }

        /**
         * @description Deletes the provided cell and the incoming and outgoing connectors
         * @param {*} cell
         * @param {*} grid
         */
        deleteCellAndConnectors(cell, grid, viewInstance) {
            const scope = this;
            let oldState = null;
            let multipleEmits = null;
            if (viewInstance) {
                oldState = viewInstance.getDiagramData();
            }
            const pluginUuid = cell.getContentInstance().uuid;
            const pluginID = cell.getContentInstance().id || cell.getContentInstance().data.id;
            const cellData = this.cells.get(cell.uuid);
            if (cellData.incoming.length === 0 && cellData.outgoing.length === 0) {
                this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.PLUGIN_DELETED, { PLUGIN_ID: pluginID, PLUGIN_UUID: pluginUuid }, true);
                cellData.ref.clearContent();
                this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.PLUGIN_DELETED, { PLUGIN_ID: pluginID, PLUGIN_UUID: pluginUuid }, null, true);
            } else {
                const payload = [];
                payload.push({ event: DIAGRAM_EMIT_EVENTS.PLUGIN_DELETED, payload: { oldState, PLUGIN_ID: pluginID, PLUGIN_UUID: pluginUuid } });
                [...cellData.incoming, ...cellData.outgoing].forEach(connectorUuid => {
                    const connector = this.connectors.get(connectorUuid);
                    const from = this.cells.get(connector.from).ref;
                    const fromContent = from.getContentInstance();
                    const to = this.cells.get(connector.to).ref;
                    const toContent = to.getContentInstance();
                    payload.push({
                        event: DIAGRAM_EMIT_EVENTS.CONNECTOR_DELETED,
                        payload: {
                            oldState,
                            CONNECTOR_UUID: connector.instance.uuid,
                            FROM_UUID: fromContent.uuid,
                            FROM_ID: fromContent.data.id,
                            TO_UUID: toContent.uuid,
                            TO_ID: toContent.data.id
                        }
                    });
                });
                multipleEmits = payload;
            }

            this.connectors.forEach((currentConnector, index) => {
                if (currentConnector.from === cell.uuid || currentConnector.to === cell.uuid) {
                    const fromCell = this.cells.get(currentConnector.from);
                    const toCell = this.cells.get(currentConnector.to);

                    const connectorUuid = currentConnector.instance.uuid;
                    const fromCellConnectorIndex = fromCell.outgoing.indexOf(connectorUuid);
                    const toCellConnectorIndex = toCell.incoming.indexOf(connectorUuid);

                    if (fromCellConnectorIndex !== -1) {
                        fromCell.outgoing.splice(fromCellConnectorIndex, 1);
                    }
                    if (toCellConnectorIndex !== -1) {
                        toCell.incoming.splice(toCellConnectorIndex, 1);
                    }

                    scope.connectors.delete(index);
                    currentConnector.passesThrough.forEach(passesElement => {
                        const cell = this.cells.get(passesElement);
                        const connectorUuid = currentConnector.instance.uuid;
                        const incomingIndex = cell.incoming.indexOf(connectorUuid);
                        if (incomingIndex !== -1) {
                            cell.incoming.splice(incomingIndex, 1);
                        }
                        const outgoingIndex = cell.outgoing.indexOf(connectorUuid);
                        if (outgoingIndex !== -1) {
                            cell.outgoing.splice(outgoingIndex, 1);
                        }
                        cell.ref.draw();
                    });
                }
            });
            return multipleEmits;
        }
    }
    return ConnectingLineHandler;
}());
